import React, { FC, useMemo } from 'react';
import clsx from 'clsx';
import { uniq } from 'lodash';

import { FormattedMessage } from '@futrli/localisation';

import { SIZES } from '../../../../core/constants';
import { StickyHeaders } from '../../predictions/components/sticky-headers';
import { HeaderRows } from '../../reports/data-grid/header-rows';
import { DataGrid } from '../../../../ui/data-grid/components/data-grid';
import { Rows } from '../../reports/data-grid/rows';
import { useDetailContext } from '../../context/detail-context/detail-context';

import styles from '../performance.scss';

interface Props {
  hideTitle?: boolean;
  isOnReporting?: boolean;
  headlineHeaderRef: React.MutableRefObject<HTMLDivElement | null>;
  tableForwardRef: React.MutableRefObject<HTMLDivElement | null>;
  updateScrollLeft: (value: number) => void;
}

export const CashFlowGrid: FC<Props> = ({
  hideTitle,
  isOnReporting,
  headlineHeaderRef,
  tableForwardRef,
  updateScrollLeft
}: Props) => {
  const {
    forecastReportV2,
    isFetchingReport,
    periodType,
    totalPastDates,
    fetchForecastReport,
    totalCashPredictRow: totalCashPredictRowV2
  } = useDetailContext();

  const { totalCashPredictRow, columns } = useMemo(() => {
    if (totalCashPredictRowV2) {
      const dailyColumns = totalCashPredictRowV2.dailyColumns || [];
      const monthlyColumnCount = totalCashPredictRowV2.account.period_data.length;
      let columns: string[] | undefined = [];

      if (dailyColumns.length) {
        // eslint-disable-next-line no-magic-numbers
        columns = uniq(dailyColumns.map(column => `${column.substring(0, 7)}-01`));
      } else if (monthlyColumnCount) {
        columns = forecastReportV2?.data.columns.slice(-monthlyColumnCount);
      }

      return {
        totalCashPredictRow: [totalCashPredictRowV2],
        columns
      };
    }

    return {};
  }, [forecastReportV2, totalCashPredictRowV2]);

  if (!totalCashPredictRow || !columns?.length) {
    return null;
  }

  return (
    <section data-testid="predicted-cash-grid" className={clsx(isOnReporting && styles.onReporting)}>
      {!hideTitle && (
        <h2 className={clsx(styles.sectionTitle, 'uiTypeHeaderFont20')}>
          <FormattedMessage id="predict.predicted_daily_cash_flow" defaultMessage="Predicted daily cash flow" />
        </h2>
      )}

      <StickyHeaders columns={columns} headerRef={headlineHeaderRef} />
      <DataGrid
        isLoading={isFetchingReport}
        totalPastDates={totalPastDates}
        headers={<HeaderRows totalRows={totalCashPredictRow} rowSize={!isOnReporting ? SIZES.LARGE : SIZES.X_LARGE} />}
        rows={
          <Rows
            totalRows={totalCashPredictRow}
            rowHeightOverride={!isOnReporting ? SIZES.LARGE : SIZES.X_LARGE}
            showZeroAndHighlightForChart={true}
            columns={columns}
            autoScrollMarker={true}
          />
        }
        tableForwardRef={tableForwardRef}
        onScroll={updateScrollLeft}
        periodType={periodType}
        isMainHeadlineGrid={true}
        onScrollEnd={fetchForecastReport}
      />
    </section>
  );
};
